import {colorEqual, SceneNodes} from "@cm/template-nodes"
import {mathIsEqual, setThreeObjectPart, ThreeObject, updateTransform} from "@template-editor/helpers/three-object"
import {Three as THREE, ThreeTsl as TSL} from "@cm/material-nodes/three"
import {DisplayMode, ThreeSceneManagerService} from "@template-editor/services/three-scene-manager.service"
import {anyDifference, objectFieldsDifferent} from "@template-editor/helpers/change-detection"
import {configThreeHelperObjectLayers} from "@template-editor/helpers/helper-objects"
import {getLtcTextures, ltcEvaluateDisk, ltcUv} from "@template-editor/helpers/ltc-disk-light"

export class SunLight extends THREE.DirectionalLight {
    angle = 0
}

class SunLightNode extends THREE.DirectionalLightNode {
    constructor(public override light: SunLight | null) {
        super(light)
    }

    private readonly angleNode = TSL.uniform(0)

    override update(frame: THREE.NodeFrame): boolean | undefined {
        const result = super.update(frame)
        this.angleNode.value = this.light?.angle ?? 0
        return result
    }

    override setupDirect(builder: THREE.NodeBuilder) {
        const {lightDirection, lightColor} = super.setupDirect(builder) as {lightDirection: THREE.Node<"vec3">; lightColor: THREE.Node<"vec3">}
        const centerDir = lightDirection // view-space direction toward the sun

        // Model the sun as an LTC disk one unit away subtending its angular diameter, radius = tan(angle/2), so the
        // angle controls the reflection size. radiance = irradiance / (πr²) so radiance × form-factor reproduces the
        // directional irradiance (small angle -> tiny bright disk -> sharp mirror highlight).
        const radius = this.angleNode
            .mul(Math.PI / 180 / 2)
            .tan()
            .max(0.001)
        const radiance = lightColor.div(radius.mul(radius).mul(Math.PI))

        const center = TSL.positionView.add(centerDir)
        const ref = centerDir.y
            .abs()
            .lessThan(0.999)
            .select(TSL.vec3(0, 1, 0), TSL.vec3(1, 0, 0))
        const axisU = ref.cross(centerDir).normalize().mul(radius)
        const axisV = axisU.div(radius).cross(centerDir).mul(radius)

        const {ltc1, ltc2} = getLtcTextures(builder)
        const uv = ltcUv(TSL.normalView, TSL.positionViewDirection, TSL.roughness)
        const t1 = ltc1.sample(uv)
        const t2 = ltc2.sample(uv)
        const mInv = TSL.mat3(TSL.vec3(t1.x, 0, t1.y), TSL.vec3(0, 1, 0), TSL.vec3(t1.z, 0, t1.w))

        const f0 = TSL.property("color", "SpecularColorBlended") as unknown as THREE.Node<"vec3">
        const fresnel = f0.mul(t2.x).add(TSL.specularF90.sub(f0).mul(t2.y))
        const diffuseContribution = TSL.property("vec3", "DiffuseContribution") as unknown as THREE.Node<"vec3">

        const identity = TSL.mat3(1, 0, 0, 0, 1, 0, 0, 0, 1)
        const ltcSpecular = radiance.mul(fresnel).mul(ltcEvaluateDisk(mInv, center, axisU, axisV, 32))
        const ltcDiffuse = radiance.mul(diffuseContribution).mul(ltcEvaluateDisk(identity, center, axisU, axisV, 8))

        return {lightColor: radiance, lightDirection: centerDir, ltcDiffuse, ltcSpecular}
    }
}

const GIZMO_RADIUS = 20
const GIZMO_RAY_LENGTH = 60
const GIZMO_RAY_COUNT = 8

export class ThreeSunLight extends ThreeObject<SceneNodes.SunLight> {
    protected override renderObject: THREE.Group = new THREE.Group()
    private threeLight: SunLight = new SunLight(0xffffff, 1)
    private sunLightHelper: ThreeSunLightHelper

    constructor(threeSceneManagerService: ThreeSceneManagerService, onAsyncUpdate: () => void) {
        super(threeSceneManagerService, onAsyncUpdate)
        this.sunLightHelper = new ThreeSunLightHelper(this, threeSceneManagerService, onAsyncUpdate)

        const renderer = threeSceneManagerService.$renderer()
        if (!renderer) throw new Error("ThreeSunLight: renderer not available")
        if (!renderer.library.lightNodes.has(SunLight)) {
            renderer.library.addLight(SunLightNode, SunLight)
        }

        this.threeLight.target.position.set(0, 0, -1)
        this.threeLight.add(this.threeLight.target)

        this.renderObject.add(this.threeLight)
        this.renderObject.add(this.sunLightHelper.getRenderObject())

        this.onDisplayModeChange(threeSceneManagerService.$displayMode())
    }

    override onDisplayModeChange(displayMode: DisplayMode) {
        this.sunLightHelper.getRenderObject().visible = displayMode === "editor"
    }

    override setup(sceneNode: SceneNodes.SunLight) {
        return anyDifference([
            objectFieldsDifferent(
                sceneNode,
                this.parameters,
                ["on", "color", "intensity", "angle"],
                (valueA, valueB) => {
                    if (typeof valueA === "object" && typeof valueB === "object") return colorEqual(valueA, valueB)
                    return valueA === valueB
                },
                ({on, color, intensity, angle}) => {
                    this.threeLight.visible = on
                    this.threeLight.color.setRGB(...color)
                    this.threeLight.intensity = intensity
                    this.threeLight.castShadow = true
                    this.threeLight.angle = angle
                },
            ),

            objectFieldsDifferent(
                sceneNode,
                this.parameters,
                ["transform"],
                (valueA, valueB) => mathIsEqual(valueA, valueB),
                ({transform}) => {
                    updateTransform(transform, this.threeLight)
                },
            ),

            this.sunLightHelper.update(sceneNode),
        ])
    }

    override dispose() {
        this.sunLightHelper.dispose()
        this.threeLight.dispose()
    }
}

class ThreeSunLightHelper extends ThreeObject<SceneNodes.SunLight> {
    protected override renderObject: THREE.Group = new THREE.Group()
    private localMesh = new THREE.Group()

    private proxyDisk: THREE.Line<THREE.BufferGeometry, THREE.LineBasicMaterial>
    private proxyFrontMesh: THREE.Mesh<THREE.CircleGeometry, THREE.MeshBasicMaterial>
    private proxyBackMesh: THREE.Mesh<THREE.CircleGeometry, THREE.MeshBasicMaterial>
    private proxyRays: THREE.LineSegments<THREE.BufferGeometry, THREE.LineBasicMaterial>

    constructor(sunLight: ThreeSunLight, threeSceneManagerService: ThreeSceneManagerService, onAsyncUpdate: () => void) {
        super(threeSceneManagerService, onAsyncUpdate)

        {
            const positions: number[] = []
            const segments = 48

            for (let i = 0; i <= segments; i++) {
                const a = (i / segments) * Math.PI * 2
                positions.push(Math.cos(a) * GIZMO_RADIUS, Math.sin(a) * GIZMO_RADIUS, 0)
            }
            const geometry = new THREE.BufferGeometry()
            geometry.setAttribute("position", new THREE.Float32BufferAttribute(positions, 3))
            this.proxyDisk = new THREE.Line(geometry, new THREE.LineBasicMaterial({fog: false}))
        }

        this.proxyFrontMesh = new THREE.Mesh(new THREE.CircleGeometry(GIZMO_RADIUS, 48), new THREE.MeshBasicMaterial({side: THREE.BackSide, fog: false}))
        this.proxyBackMesh = new THREE.Mesh(
            new THREE.CircleGeometry(GIZMO_RADIUS, 48),
            new THREE.MeshBasicMaterial({side: THREE.FrontSide, color: 0x080808, fog: false}),
        )

        {
            const geometry = new THREE.BufferGeometry()
            geometry.setAttribute("position", new THREE.Float32BufferAttribute((GIZMO_RAY_COUNT + 1) * 2 * 3, 3))
            this.proxyRays = new THREE.LineSegments(geometry, new THREE.LineBasicMaterial({color: 0x666666, fog: false}))
        }

        this.localMesh.add(this.proxyDisk)
        setThreeObjectPart(this.proxyDisk, sunLight)
        this.localMesh.add(this.proxyFrontMesh)
        setThreeObjectPart(this.proxyFrontMesh, sunLight)
        this.localMesh.add(this.proxyBackMesh)
        setThreeObjectPart(this.proxyBackMesh, sunLight)
        this.localMesh.add(this.proxyRays)

        this.renderObject.add(this.localMesh)

        configThreeHelperObjectLayers(this.getRenderObject())
    }

    setup(sceneNode: SceneNodes.SunLight) {
        return anyDifference([
            objectFieldsDifferent(
                sceneNode,
                this.parameters,
                ["color", "intensity"],
                (valueA, valueB) => {
                    if (typeof valueA === "object" && typeof valueB === "object") return colorEqual(valueA, valueB)
                    return valueA === valueB
                },
                ({color, intensity}) => {
                    this.proxyDisk.material.color.copy(new THREE.Color(...color).multiplyScalar(intensity))
                    this.proxyFrontMesh.material.color.copy(this.proxyDisk.material.color)
                },
            ),
            objectFieldsDifferent(sceneNode, this.parameters, ["angle", "intensity"], undefined, ({angle, intensity}) => {
                this.updateRays(angle, intensity)
            }),
            objectFieldsDifferent(
                sceneNode,
                this.parameters,
                ["transform"],
                (valueA, valueB) => mathIsEqual(valueA, valueB),
                ({transform}) => {
                    updateTransform(transform, this.localMesh)
                },
            ),
        ])
    }

    private updateRays(angle: number, intensity: number) {
        const buffer = this.proxyRays.geometry.getAttribute("position") as THREE.Float32BufferAttribute

        const halfAngle = (angle * Math.PI) / 180 / 2
        const sin = Math.sin(halfAngle)
        const cos = Math.cos(halfAngle)

        const length = Math.min(GIZMO_RAY_LENGTH * 0.5 + intensity * 12, GIZMO_RAY_LENGTH * 2.5)
        for (let i = 0; i < GIZMO_RAY_COUNT; i++) {
            const a = (i / GIZMO_RAY_COUNT) * Math.PI * 2
            const rx = Math.cos(a)
            const ry = Math.sin(a)
            buffer.setXYZ(2 * i, rx * GIZMO_RADIUS, ry * GIZMO_RADIUS, 0)
            buffer.setXYZ(2 * i + 1, rx * (GIZMO_RADIUS + sin * length), ry * (GIZMO_RADIUS + sin * length), -cos * length)
        }
        buffer.setXYZ(2 * GIZMO_RAY_COUNT, 0, 0, 0)
        buffer.setXYZ(2 * GIZMO_RAY_COUNT + 1, 0, 0, -length)
        buffer.needsUpdate = true
    }

    override dispose() {
        this.proxyDisk.geometry.dispose()
        this.proxyDisk.material.dispose()
        this.proxyFrontMesh.geometry.dispose()
        this.proxyFrontMesh.material.dispose()
        this.proxyBackMesh.geometry.dispose()
        this.proxyBackMesh.material.dispose()
        this.proxyRays.geometry.dispose()
        this.proxyRays.material.dispose()
    }
}
